// src/graphql/GraphQLProvider.jsx
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useAuth } from '../components/AuthWrapper';
import { executeGraphQLRequest, isAuthenticationError, extractErrorMessage } from './client';
import { normalizeJobsData } from './normalizers';
import * as allQueries from './queries';
import * as allMutations from './mutations';

const GraphQLContext = createContext();

export const useGraphQL = () => useContext(GraphQLContext);

// Static maps — defined once at module load. Hoisting these out of the component
// keeps their identities stable across renders, which matters because consumers
// commonly include `queries` / `mutations` in `useEffect` / `useCallback` deps.
// If we rebuilt them every render, every fetch (which toggles `loading`) would
// re-render the provider, hand consumers new references, re-fire their effects,
// and trigger another fetch — an infinite request storm.
const queries = {
    // Engagement campaigns
    getEngagementCampaigns: allQueries.getEngagementCampaigns,
    getEngagementPreview: allQueries.getEngagementPreview,
    getEngagementSends: allQueries.getEngagementSends,
    getStats: allQueries.getStats,
    getUsers: allQueries.getUsers,
    getUser: allQueries.getUser,
    getCompanies: allQueries.getCompanies,
    getCompany: allQueries.getCompany,
    getCompanyFingerprint: allQueries.getCompanyFingerprint,
    getTenders: allQueries.getTenders,
    getTender: allQueries.getTender,
    getAssessmentByTender: allQueries.getAssessmentByTender,
    searchTenders: allQueries.searchTenders,
    getTopRatedTenders: allQueries.getTopRatedTenders,
    getCompanyTenderRating: allQueries.getCompanyTenderRating,
    getTenderAssessments: allQueries.getTenderAssessments,
    getTenderAssessmentsCount: allQueries.getTenderAssessmentsCount,
    searchTendersFTS: allQueries.searchTendersFTS,
    getEvidenceImages: allQueries.getEvidenceImages,
    getJob: allQueries.getJob,
    getJobs: allQueries.getJobs,
    getJobTimeline: allQueries.getJobTimeline,
    getJobEvents: allQueries.getJobEvents,
    getAssessmentCycleStatus: allQueries.getAssessmentCycleStatus,
    getFingerprintAssessmentStatus: allQueries.getFingerprintAssessmentStatus,
    getRecentlyProcessedAssessments: allQueries.getRecentlyProcessedAssessments,
    getSubscriptions: allQueries.getSubscriptions,
    getSubscriptionByCompany: allQueries.getSubscriptionByCompany,
    getModulesBySubscription: allQueries.getModulesBySubscription,
    getSessions: allQueries.getSessions,
    getSession: allQueries.getSession,
    getDocumentStorageStats: allQueries.getDocumentStorageStats,
    getEvidenceCacheStatus: allQueries.getEvidenceCacheStatus,
    getDocumentIntegrityCheck: allQueries.getDocumentIntegrityCheck,
    getFeatureCatalog: allQueries.getFeatureCatalog,
    getCompanyFeatureOverrides: allQueries.getCompanyFeatureOverrides,
    getAllFeatureOverrides: allQueries.getAllFeatureOverrides,
    getAdminTeamInfo: allQueries.getAdminTeamInfo,
    getAdminTeamInvitations: allQueries.getAdminTeamInvitations,
    validateInvitationToken: allQueries.validateInvitationToken,
    getDeepCrawlSiteConfigs: allQueries.getDeepCrawlSiteConfigs,
    getDeepCrawlSiteConfig: allQueries.getDeepCrawlSiteConfig,
    getDeepCrawlTelemetry: allQueries.getDeepCrawlTelemetry,
    getDeepCrawlStats: allQueries.getDeepCrawlStats,
    // Market trend queries (global narrative-profile signals)
    getTopTags: allQueries.getTopTags,
    getTopDomains: allQueries.getTopDomains,
    getNarrativeStats: allQueries.getNarrativeStats,
    getBudgetByDomain: allQueries.getBudgetByDomain,
    // LLM tender artifacts
    getTenderNarrativeAssessment: allQueries.getTenderNarrativeAssessment,
    getTenderFacetAnalysis: allQueries.getTenderFacetAnalysis,
    // Tender download bundles
    getTenderDownload: allQueries.getTenderDownload,
    // Pricing
    getPricingTiers: allQueries.getPricingTiers,
    getCpvCodes: allQueries.getCpvCodes,
    getCpvCode: allQueries.getCpvCode,
    getCpvParents: allQueries.getCpvParents,
    getCpvChildren: allQueries.getCpvChildren,
    getNutsCodes: allQueries.getNutsCodes,
    getNutsCode: allQueries.getNutsCode,
    getAutocomplete: allQueries.getAutocomplete,
    getCpvTree: allQueries.getCpvTree,
    getPermissions: allQueries.getPermissions,
    getRolePermissions: allQueries.getRolePermissions,
    getBetaSignups: allQueries.getBetaSignups,
    getSystemHealth: allQueries.getSystemHealth,
    getSystemConfig: allQueries.getSystemConfig,
    getImpersonationInfo: allQueries.getImpersonationInfo,
    getSavedSearchReportLogs: allQueries.getSavedSearchReportLogs,
    // Workflow queries
    getWorkflowTemplates: allQueries.getWorkflowTemplates,
    getWorkflowTemplate: allQueries.getWorkflowTemplate,
    getWorkflowRun: allQueries.getWorkflowRun,
    getWorkflowRuns: allQueries.getWorkflowRuns,
    getWorkflowTemplateYAML: allQueries.getWorkflowTemplateYAML,
    // V32: Stale job detection
    getStaleJobs: allQueries.getStaleJobs,
    // Job schema queries (dynamic job type definitions from backend)
    getJobTypeSchemas: allQueries.getJobTypeSchemas,
    getJobTypeSchema: allQueries.getJobTypeSchema,
    // Event action queries
    getEventActionBindings: allQueries.getEventActionBindings,
    getEventActionBinding: allQueries.getEventActionBinding,
    getEventActionLogs: allQueries.getEventActionLogs,
    getEventActionStats: allQueries.getEventActionStats,
    // Event type schema queries (dynamic event type definitions from backend)
    getEventTypeSchemas: allQueries.getEventTypeSchemas,
    getEventTypeSchema: allQueries.getEventTypeSchema,
    // Sales cockpit queries
    getSalesProspects: allQueries.getSalesProspects,
    getSalesProspect: allQueries.getSalesProspect,
    getSalesProspectMatches: allQueries.getSalesProspectMatches,
    getCrmCallList: allQueries.getCrmCallList,
};

const mutations = {
    createUser: allMutations.createUser,
    updateUser: allMutations.updateUser,
    deleteUser: allMutations.deleteUser,
    setCompanyRepresentative: allMutations.setCompanyRepresentative,
    createCompany: allMutations.createCompany,
    updateCompany: allMutations.updateCompany,
    adminUpdateCompany: allMutations.adminUpdateCompany,
    deleteCompany: allMutations.deleteCompany,
    createCompanyFingerprint: allMutations.createCompanyFingerprint,
    updateCompanyFingerprint: allMutations.updateCompanyFingerprint,
    deleteCompanyFingerprint: allMutations.deleteCompanyFingerprint,
    generateFingerprintFromDescription: allMutations.generateFingerprintFromDescription,
    createTender: allMutations.createTender,
    updateTender: allMutations.updateTender,
    deleteTender: allMutations.deleteTender,
    updateCompanyTenderRatings: allMutations.updateCompanyTenderRatings,
    requestAssessment: allMutations.requestAssessment,
    prequalifyTenders: allMutations.prequalifyTenders,
    // Job trigger mutations
    adminCrawlTender: allMutations.adminCrawlTender,
    adminCrawlAllTenders: allMutations.adminCrawlAllTenders,
    adminSyncTenders: allMutations.adminSyncTenders,
    adminCheckNewTenders: allMutations.adminCheckNewTenders,
    adminRefreshDocuments: allMutations.adminRefreshDocuments,
    adminAssessTender: allMutations.adminAssessTender,
    adminAssessAllTenders: allMutations.adminAssessAllTenders,
    generateTenderNarrativeAssessment: allMutations.generateTenderNarrativeAssessment,
    adminGeocodeTender: allMutations.adminGeocodeTender,
    adminGeocodeAllTenders: allMutations.adminGeocodeAllTenders,
    adminExtractDocumentText: allMutations.adminExtractDocumentText,
    adminExtractTenderDocumentsText: allMutations.adminExtractTenderDocumentsText,
    adminExtractAllDocumentsText: allMutations.adminExtractAllDocumentsText,
    adminConvertTenderDocuments: allMutations.adminConvertTenderDocuments,
    adminConvertAllDocuments: allMutations.adminConvertAllDocuments,
    adminDeepCrawlTender: allMutations.adminDeepCrawlTender,
    adminDeepCrawlAll: allMutations.adminDeepCrawlAll,
    adminDeepCrawlByDomain: allMutations.adminDeepCrawlByDomain,
    adminIndexTender: allMutations.adminIndexTender,
    adminIndexAllTenders: allMutations.adminIndexAllTenders,
    adminSearchCorpus: allMutations.adminSearchCorpus,
    adminRescoreCorpusV3: allMutations.adminRescoreCorpusV3,
    adminRecreateIndex: allMutations.adminRecreateIndex,
    adminReverseRecreateIndex: allMutations.adminReverseRecreateIndex,
    adminReverseIndexAllFingerprints: allMutations.adminReverseIndexAllFingerprints,
    // Job management mutations
    adminStartJob: allMutations.adminStartJob,
    adminCompleteJob: allMutations.adminCompleteJob,
    adminFailJob: allMutations.adminFailJob,
    adminRetryJob: allMutations.adminRetryJob,
    adminCancelJob: allMutations.adminCancelJob,
    adminCancelPendingJobsByCompany: allMutations.adminCancelPendingJobsByCompany,
    adminCleanupOldJobs: allMutations.adminCleanupOldJobs,
    adminFixStuckJobs: allMutations.adminFixStuckJobs,
    adminGetPendingJobs: allMutations.adminGetPendingJobs,
    adminEnableScheduledJob: allMutations.adminEnableScheduledJob,
    adminDisableScheduledJob: allMutations.adminDisableScheduledJob,
    adminUpdateScheduledJob: allMutations.adminUpdateScheduledJob,
    adminTriggerScheduledJob: allMutations.adminTriggerScheduledJob,
    // V32: Stale job detection
    adminResetStaleJobs: allMutations.adminResetStaleJobs,
    // Subscription mutations
    createSubscription: allMutations.createSubscription,
    updateSubscription: allMutations.updateSubscription,
    cancelSubscription: allMutations.cancelSubscription,
    // Session mutations
    adminDeleteSession: allMutations.adminDeleteSession,
    adminImpersonateUser: allMutations.adminImpersonateUser,
    endImpersonation: allMutations.endImpersonation,
    // Storage mutations
    adminClearEvidenceCache: allMutations.adminClearEvidenceCache,
    adminClearAssessmentEvidenceCache: allMutations.adminClearAssessmentEvidenceCache,
    adminRegenerateEvidenceImages: allMutations.adminRegenerateEvidenceImages,
    adminCleanupOrphanedFiles: allMutations.adminCleanupOrphanedFiles,
    adminVerifyDocumentStorage: allMutations.adminVerifyDocumentStorage,
    adminListTenderDocuments: allMutations.adminListTenderDocuments,
    adminDeleteDocument: allMutations.adminDeleteDocument,
    adminDeleteAllTenderDocuments: allMutations.adminDeleteAllTenderDocuments,
    // Feature mutations
    setFeatureOverride: allMutations.setFeatureOverride,
    removeFeatureOverride: allMutations.removeFeatureOverride,
    // Team mutations
    adminInviteTeamMember: allMutations.adminInviteTeamMember,
    adminCancelInvitation: allMutations.adminCancelInvitation,
    adminRespondToJoinRequest: allMutations.adminRespondToJoinRequest,
    adminRemoveTeamMember: allMutations.adminRemoveTeamMember,
    adminTransferRepresentative: allMutations.adminTransferRepresentative,
    adminSetCompanyUserLimit: allMutations.adminSetCompanyUserLimit,
    // Deep crawl mutations
    deleteDeepCrawlSiteConfig: allMutations.deleteDeepCrawlSiteConfig,
    cleanupDeepCrawlTelemetry: allMutations.cleanupDeepCrawlTelemetry,
    // Tender download bundles
    requestTenderDownload: allMutations.requestTenderDownload,
    // Saved search mutations
    adminSendSavedSearchReports: allMutations.adminSendSavedSearchReports,
    // TED query generation
    adminGenerateTedQuery: allMutations.adminGenerateTedQuery,
    // Document normalization
    adminNormalizeDocuments: allMutations.adminNormalizeDocuments,
    // Transactional email
    adminSendEmail: allMutations.adminSendEmail,
    // Admin support action mutations
    adminResendVerificationEmail: allMutations.adminResendVerificationEmail,
    adminResendPasswordResetEmail: allMutations.adminResendPasswordResetEmail,
    adminResendRegistrationEmail: allMutations.adminResendRegistrationEmail,
    adminSetUserVerified: allMutations.adminSetUserVerified,
    adminSetUserPassword: allMutations.adminSetUserPassword,
    // Workflow job mutations
    adminRunWorkflow: allMutations.adminRunWorkflow,
    adminRunCompanyWorkflows: allMutations.adminRunCompanyWorkflows,
    // Generic job trigger (fallback for job types without a dedicated mutation)
    adminEnqueueJob: allMutations.adminEnqueueJob,
    // Workflow mutations
    startWorkflow: allMutations.startWorkflow,
    cancelWorkflow: allMutations.cancelWorkflow,
    createWorkflowTemplate: allMutations.createWorkflowTemplate,
    updateWorkflowTemplate: allMutations.updateWorkflowTemplate,
    deleteWorkflowTemplate: allMutations.deleteWorkflowTemplate,
    saveWorkflowYAML: allMutations.saveWorkflowYAML,
    reloadWorkflowTemplates: allMutations.reloadWorkflowTemplates,
    reconcileSchedule: allMutations.reconcileSchedule,
    // Event action mutations
    adminCreateEventActionBinding: allMutations.adminCreateEventActionBinding,
    adminUpdateEventActionBinding: allMutations.adminUpdateEventActionBinding,
    adminDeleteEventActionBinding: allMutations.adminDeleteEventActionBinding,
    adminEnableEventActionBinding: allMutations.adminEnableEventActionBinding,
    adminDisableEventActionBinding: allMutations.adminDisableEventActionBinding,
    adminCleanupEventActionLogs: allMutations.adminCleanupEventActionLogs,
    // Sales cockpit mutations
    importSalesProspects: allMutations.importSalesProspects,
    updateSalesProspect: allMutations.updateSalesProspect,
    generateSalesProspectProfile: allMutations.generateSalesProspectProfile,
    sendSalesTeaser: allMutations.sendSalesTeaser,
    crmLogCall: allMutations.crmLogCall,
    crmGeocodeCompanies: allMutations.crmGeocodeCompanies,
    // Engagement campaigns
    sendEngagementMail: allMutations.sendEngagementMail,
    applyEngagementSunset: allMutations.applyEngagementSunset,
};

export const GraphQLProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { token, logout } = useAuth();

    /**
     * Execute jobs query with proper pagination and filtering
     * Uses inline query with interpolated values due to backend enum handling
     */
    const executeJobsQuery = useCallback(async (variables = {}) => {
        try {
            // Build query args - only include defined values
            const args = [];
            args.push(`limit: ${variables.limit || 50}`);
            args.push(`offset: ${variables.offset || 0}`);

            // Status filter - backend expects uppercase enum value
            if (variables.status && variables.status !== '') {
                args.push(`status: ${variables.status.toUpperCase()}`);
            }

            // Type filter - backend expects uppercase enum value
            if (variables.type && variables.type !== '') {
                args.push(`type: ${variables.type.toUpperCase()}`);
            }

            // isScheduled filter
            if (variables.isScheduled !== undefined && variables.isScheduled !== null) {
                args.push(`isScheduled: ${variables.isScheduled}`);
            }

            const simpleQuery = `{
                adminJobs(${args.join(', ')}) {
                    id
                    type
                    status
                    tenderId
                    parameters
                    result
                    error
                    startedAt
                    completedAt
                    cronSchedule
                    enabled
                    isScheduled
                    lastScheduledRun
                    nextScheduledRun
                    createdAt
                    updatedAt
                    progressPercent
                    progressMessage
                    retryCount
                    maxRetries
                    cancelRequested
                    cancelledAt
                    timeoutSeconds
                    parentJobId
                    heartbeatAt
                    workerId
                }
            }`;

            const result = await executeGraphQLRequest(simpleQuery, {}, token);

            if (result.errors) {
                console.error('Jobs query failed:', result.errors);
                return { data: { adminJobs: [] } };
            }

            return { data: { adminJobs: result.data?.adminJobs || [] } };
        } catch (err) {
            console.error('Jobs query completely failed:', err);
            return { data: { adminJobs: [] } };
        }
    }, [token]);

    /**
     * Execute a GraphQL query
     */
    const executeQuery = useCallback(async (query, variables = {}) => {
        setLoading(true);
        setError(null);

        try {
            // Special handling for jobs queries
            if (query.includes('adminJobs')) {
                const result = await executeJobsQuery(variables);
                return normalizeJobsData(result.data);
            }

            // Regular query execution for non-jobs queries
            const result = await executeGraphQLRequest(query, variables, token);

            if (result.errors) {
                if (isAuthenticationError(result)) {
                    logout();
                    throw new Error('Session expired. Please login again.');
                }

                const errorMessage = extractErrorMessage(result);
                throw new Error(errorMessage);
            }

            return result.data;
        } catch (err) {
            if (err.message === 'AUTHENTICATION_EXPIRED') {
                logout();
                throw new Error('Authentication expired. Please login again.');
            }
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [token, logout, executeJobsQuery]);

    /**
     * Execute a GraphQL mutation
     */
    const executeMutation = useCallback(async (mutation, variables = {}) => {
        return executeQuery(mutation, variables);
    }, [executeQuery]);

    const value = useMemo(() => ({
        executeQuery,
        executeMutation,
        queries,
        mutations,
        loading,
        error,
    }), [executeQuery, executeMutation, loading, error]);

    return (
        <GraphQLContext.Provider value={value}>
            {children}
        </GraphQLContext.Provider>
    );
};

export default GraphQLProvider;
